/**
 * User (staff/employee) Service — admin user-management API.
 * Backs the /users admin page. All endpoints are owner/admin-gated on the
 * backend (see app.py /employees routes); the frontend route is guarded too.
 */
import { apiFetch } from '../lib/apiFetch';

export type UserRole = 'owner' | 'admin' | 'dispatcher' | 'office' | 'driver';

export const USER_ROLES: { value: UserRole; label: string }[] = [
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'dispatcher', label: 'Dispatcher' },
  { value: 'office', label: 'Office' },
  { value: 'driver', label: 'Driver' },
];

export interface StaffUser {
  id: string;
  email: string;
  name: string | null;
  firstname: string | null;
  lastname: string | null;
  role: UserRole;
  active: boolean;
  phone: string | null;
  job_title: string | null;
  department: string | null;
  avatar_url: string | null;
  has_password: boolean;
  last_login_at: string | null;
  deactivated_at: string | null;
  created_at: string | null;
}

export interface CreateUserInput {
  email: string;
  firstname: string;
  lastname: string;
  role: UserRole;
  password: string;
  phone?: string;
  job_title?: string;
  department?: string;
}

export interface UpdateUserInput {
  email?: string;
  firstname?: string;
  lastname?: string;
  role?: UserRole;
  phone?: string;
  job_title?: string;
  department?: string;
}

async function parseError(response: Response, fallback: string): Promise<never> {
  const body = await response.json().catch(() => ({}));
  throw new Error(body.message || fallback);
}

export const fetchUsers = async (includeInactive: boolean): Promise<StaffUser[]> => {
  const response = await apiFetch(`/employees?include_inactive=${includeInactive ? 1 : 0}`);
  if (!response.ok) await parseError(response, 'Failed to load users');
  const data = await response.json();
  return data.data || [];
};

export const createUser = async (input: CreateUserInput): Promise<StaffUser> => {
  const response = await apiFetch('/employees', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) await parseError(response, 'Failed to create user');
  return (await response.json()).data;
};

export const updateUser = async (id: string, input: UpdateUserInput): Promise<StaffUser> => {
  const response = await apiFetch(`/employees/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) await parseError(response, 'Failed to update user');
  return (await response.json()).data;
};

export const resetUserPassword = async (id: string, password: string): Promise<void> => {
  const response = await apiFetch(`/employees/${id}/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!response.ok) await parseError(response, 'Failed to reset password');
};

export const deactivateUser = async (id: string): Promise<void> => {
  const response = await apiFetch(`/employees/${id}/deactivate`, { method: 'POST' });
  if (!response.ok) await parseError(response, 'Failed to deactivate user');
};

export const reactivateUser = async (id: string): Promise<void> => {
  const response = await apiFetch(`/employees/${id}/reactivate`, { method: 'POST' });
  if (!response.ok) await parseError(response, 'Failed to reactivate user');
};
